import * as React from "react";
import { Link, usePage } from "@inertiajs/react";
import { ChevronRight } from "lucide-react";
import type { SharedPageProps } from "@lib/inertia-types";
import { cn } from "@lib/cn";

// Breadcrumbs to the right of the project switcher: [section] > [selected item].
// The section is derived from the URL; the selected request name comes from the
// page's own props (Inertia merges page props into usePage().props), so it is
// only present on the request workspace.
export function HeaderBreadcrumbs() {
  const { url, props } = usePage<
    SharedPageProps & { selected_request?: { id: number; name: string } | null }
  >();
  const project = props.current_project ?? null;
  if (!project) return null;

  const base = `/projects/${project.slug}`;
  const path = url.split("?")[0];
  const section = sectionFor(path, base);
  if (!section) return null;

  const selectedRequest =
    section.key === "requests" ? props.selected_request ?? null : null;

  return (
    <nav
      className="flex min-w-0 items-center gap-0.5 text-small"
      aria-label="Breadcrumb"
    >
      <Separator />
      <Crumb href={section.href} current={!selectedRequest}>
        {section.label}
      </Crumb>
      {selectedRequest && (
        <>
          <Separator />
          <Crumb current>{selectedRequest.name}</Crumb>
        </>
      )}
    </nav>
  );
}

type Section = { key: string; label: string; href: string };

function sectionFor(path: string, base: string): Section | null {
  if (path.startsWith(`${base}/environments`))
    return { key: "environments", label: "Environments", href: `${base}/environments` };
  if (path.startsWith(`${base}/credentials`))
    return { key: "credentials", label: "Credentials", href: `${base}/credentials` };
  if (path.startsWith(`${base}/mocks/logs`))
    return { key: "logs", label: "Mock logs", href: `${base}/mocks/logs` };
  if (path.startsWith(`${base}/mocks`))
    return { key: "mocks", label: "Mocks", href: `${base}/mocks` };
  if (path.startsWith(`${base}/settings`))
    return { key: "settings", label: "Project settings", href: `${base}/settings` };
  if (path === base || path.startsWith(`${base}/requests`))
    return { key: "requests", label: "Requests", href: base };
  return null;
}

function Separator() {
  return (
    <ChevronRight
      className="size-3.5 shrink-0 text-cloverhound-gray-400"
      aria-hidden="true"
    />
  );
}

function Crumb({
  href,
  current,
  children,
}: {
  href?: string;
  current?: boolean;
  children: React.ReactNode;
}) {
  const base =
    "block max-w-[16rem] truncate rounded px-1.5 py-0.5 text-small transition-colors";
  if (current || !href) {
    return (
      <span
        aria-current={current ? "page" : undefined}
        className={cn(
          base,
          "font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50",
        )}
      >
        {children}
      </span>
    );
  }
  return (
    <Link
      href={href}
      className={cn(
        base,
        "text-cloverhound-gray-600 hover:bg-cloverhound-gray-100 hover:text-cloverhound-gray-900 dark:text-cloverhound-gray-400 dark:hover:bg-cloverhound-gray-850 dark:hover:text-cloverhound-gray-50",
      )}
    >
      {children}
    </Link>
  );
}
